// Local style imports
import { Grid } from '@interstate/components/Grid';
import { Typography } from '@interstate/components/Typography';
import {
    DOCUMENT_VERIFICATION_TRUST_KEY,
    RESEND_ID_REQUEST_ACTION_LABEL,
    RESEND_ID_REQUEST_CONFIRMATION,
    RESEND_ID_REQUEST_PHONE_PLACEHOLDER
} from '../../shared/constants';
import { PhoneInput } from '@interstate/components/PhoneInput';
import { Button } from '@interstate/components/Button';
import { RightAlignedGrid, GridRowContainer } from './ResendIdRequest.style';
import { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import * as complianceSelectors from '@makemydeal/dr-dash-store/selectors/complianceSelectors';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as complianceActionCreators from '@makemydeal/dr-dash-store/actions/complianceActionCreators';

import { dealLockHooks } from '../../../hooks';
import type { ComplianceSections } from '../../shared/types';
import { ComplianceStatus } from '../../shared/types';
import { ResendIdStatus } from './ResendIdStatus';
import { getResendIdRequestState } from '../../shared/utils';

export const ResendIdRequest = () => {
    const dispatch = useDispatch();
    const shopper = useSelector(offerReduxSelectors.getShopperInfo);
    const dealXgId = useSelector(offerReduxSelectors.getDealXgId);
    const isLoading = useSelector(complianceSelectors.getResendIdVerificationLoading);
    const resendIdVerificationResponse = useSelector(complianceSelectors.getResendIdVerificationData);
    const error = useSelector(complianceSelectors.getResendIdVerificationError);

    const isLocked = dealLockHooks.useIsDealLocked();

    const [phoneNumber, setPhoneNumber] = useState(shopper?.phone || '');

    const initialPhoneValid = !!(shopper?.phone && shopper.phone.trim() !== '');
    const [fieldValidationStatus, setFieldValidationStatus] = useState<{ [key: string]: boolean }>({
        'phone-number': initialPhoneValid
    });

    const participantMatch = useSelector(complianceSelectors.getComplianceParticipantForShopper);

    const data = (participantMatch?.compliance ?? {}) as Partial<ComplianceSections>;
    const IdVerificationData = data.idVerification ?? {};

    const findings = Array.isArray(IdVerificationData.findings) ? IdVerificationData.findings : [];
    const documentVerificationTrustValue = findings.find((item: any) => item.name === DOCUMENT_VERIFICATION_TRUST_KEY)?.value || '';

    const status = IdVerificationData.status || '';

    const updateFieldValidationStatus = (fieldName: string, value: boolean) => {
        setFieldValidationStatus((prevFields) => ({
            ...prevFields,
            [fieldName]: value
        }));
    };

    const cleanPhoneNumber = (phone: string): string => {
        return phone.replace(/\D/g, '');
    };

    const handleSaveButton = () => {
        if (dealXgId && phoneNumber) {
            const cleanedPhoneNumber = cleanPhoneNumber(phoneNumber);
            dispatch(complianceActionCreators.resendIdVerificationRequest(dealXgId, cleanedPhoneNumber));
        }
    };

    const isPhoneEmpty = !phoneNumber || phoneNumber.trim() === '';
    const isPhoneInvalid = fieldValidationStatus['phone-number'] === false;

    const isSendDisabled = isPhoneEmpty || isPhoneInvalid || isLoading;

    const { sendButtonDisabled } = getResendIdRequestState(status, documentVerificationTrustValue);

    const [effectiveStatus, effectiveSendButtonDisabled] = (() => {
        if (isLocked) {
            return [status, true];
        }

        if (resendIdVerificationResponse && !error) {
            return [ComplianceStatus.STARTED, true];
        }
        if (error) {
            return [ComplianceStatus.COMPLETED, false];
        }
        return [status, sendButtonDisabled];
    })();

    return (
        <>
            <GridRowContainer container mt={0.5} alignItems="center">
                <Grid xs={8}>
                    <Typography variant="strong-sm">{RESEND_ID_REQUEST_CONFIRMATION}</Typography>
                </Grid>

                <RightAlignedGrid xs={4}>
                    <ResendIdStatus status={effectiveStatus} trustValue={documentVerificationTrustValue} />
                </RightAlignedGrid>
            </GridRowContainer>

            <GridRowContainer container mt={1} alignItems="inherit">
                <Grid xs={8} key="phone">
                    <PhoneInput
                        id="phone-number"
                        displayLabel={false}
                        label="Phone Number"
                        name="phone-number"
                        placeholder={RESEND_ID_REQUEST_PHONE_PLACEHOLDER}
                        onChange={(e) => {
                            const newValue = e.target.value;
                            const isValid = e.isValid as boolean;
                            const isEmpty = !newValue;
                            updateFieldValidationStatus('phone-number', !isEmpty && isValid);
                            setPhoneNumber(newValue);
                        }}
                        value={phoneNumber}
                        data-testid="phone-number"
                        disabled={effectiveSendButtonDisabled || isLocked}
                    />
                </Grid>
                <RightAlignedGrid xs={4} key="sendButton">
                    <Button
                        buttonStyle="primary"
                        data-testid="resend-id-request-button"
                        id="resend-id-request-button"
                        size="medium"
                        disabled={isLocked || effectiveSendButtonDisabled || isSendDisabled}
                        onClick={() => {
                            handleSaveButton();
                        }}
                    >
                        {RESEND_ID_REQUEST_ACTION_LABEL}
                    </Button>
                </RightAlignedGrid>
            </GridRowContainer>
        </>
    );
};
